import axios from 'axios';

const api = axios.create({
  baseURL: `/api`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// === Helper: فك تشفير JWT ===
function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch { return null; }
}

function isAdminToken() {
  const token = localStorage.getItem('token');
  if (token) {
    const decoded = decodeToken(token);
    if (decoded?.role === 'admin') return true;
  }
  return localStorage.getItem('user_role') === 'admin';
}

// === Token handling ===
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

let isLoggingOut = false;
let isRefreshing = false;
let refreshSubscribers = [];
let refreshCooldownUntil = 0; // Timestamp: don't retry refresh before this time

function onRefreshed(newToken) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

// تسجيل خروج مع رسالة واضحة
function gracefulLogout(message) {
  if (isLoggingOut) return;
  isLoggingOut = true;
  localStorage.removeItem('token');
  localStorage.removeItem('user_role');
  // Save the message to show on login page
  if (message) {
    sessionStorage.setItem('logout_reason', message);
  }
  setTimeout(() => {
    isLoggingOut = false;
    window.location.href = '/login';
  }, 100);
}

// محاولة تجديد التوكن
async function tryRefreshToken(originalRequest) {
  // Cooldown: don't retry refresh too quickly after failure
  if (Date.now() < refreshCooldownUntil) {
    return null;
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshSubscribers.push((newToken) => {
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest._retry = true;
          resolve(api(originalRequest));
        } else {
          reject(new Error('Refresh failed'));
        }
      });
    });
  }

  isRefreshing = true;
  const currentToken = localStorage.getItem('token');
  if (!currentToken) {
    isRefreshing = false;
    return null;
  }

  try {
    const res = await axios.post('/api/auth/refresh', {}, {
      headers: { Authorization: `Bearer ${currentToken}` },
      timeout: 10000,
    });
    const newToken = res.data.access_token;
    localStorage.setItem('token', newToken);
    // Save role from response and token
    if (res.data.user?.role) localStorage.setItem('user_role', res.data.user.role);
    const decoded = decodeToken(newToken);
    if (decoded?.role) localStorage.setItem('user_role', decoded.role);

    isRefreshing = false;
    onRefreshed(newToken);

    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    originalRequest._retry = true;
    return api(originalRequest);
  } catch {
    isRefreshing = false;
    // Notify subscribers that refresh failed
    refreshSubscribers.forEach(cb => cb(null));
    refreshSubscribers = [];
    // Set cooldown: don't retry for 30 seconds
    refreshCooldownUntil = Date.now() + 30000;
    return null;
  }
}

// === Response Interceptor ===
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // خطأ شبكة - ابق الجلسة
    if (!error.response) {
      console.warn('Network error - keeping session');
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';
    const originalRequest = error.config;
    const admin = isAdminToken();

    // === 401 Handling ===
    if (status === 401 && !isLoggingOut && !originalRequest._retry) {

      // === حالة: جهاز آخر ===
      if (detail.includes('جهاز آخر')) {
        if (admin) {
          // الأدمن: لا يُطرد أبداً
          return Promise.reject(error);
        }
        // المستخدم العادي: يُطرد مع رسالة واضحة
        gracefulLogout('تم تسجيل الدخول من جهاز آخر. سجّل دخول مجدداً لاستعادة الجلسة.');
        return Promise.reject(error);
      }

      // === حالة: التوكن انتهى أو غير صالح ===
      // الخطوة 1: حاول تجدد التوكن (لكل المستخدمين)
      const result = await tryRefreshToken(originalRequest);
      if (result) return result;

      // الخطوة 2: التجديد فشل
      if (admin) {
        // الأدمن: ما يُطرد أبداً - يبقى على الصفحة
        return Promise.reject(error);
      }

      // المستخدم العادي: طرد مع رسالة ودية
      gracefulLogout('انتهت جلستك. سجّل دخول مجدداً للمتابعة.');
      return Promise.reject(error);
    }

    // === 403: اشتراك مطلوب ===
    if (status === 403) {
      if (detail === 'subscription_required' || detail === 'subscription_expired') {
        window.location.href = '/subscribe';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
